import type { Heading, Root, Text } from "mdast";
import type { Plugin } from "unified";

import { $remark } from "@milkdown/kit/utils";
import { visit } from "unist-util-visit";

import { withMeta } from "../__internal__";

interface HeadingData {
  hProperties?: {
    id?: string;
    className?: string[];
    _attrOrder?: string[];
    [key: string]: unknown;
  };
}

/// A remark plugin to parse Pandoc-style heading attributes: # Heading {#id .class key=value}
/// This extracts attributes from the heading text and stores them in node.data.hProperties.
const remarkHeadingAttrs: Plugin<[], Root> = () => {
  return (tree) => {
    visit(tree, "heading", (node: Heading) => {
      const lastChild = node.children[node.children.length - 1];
      if (!lastChild || lastChild.type !== "text") return;

      const text = (lastChild as Text).value;
      // Match {#id .class .class2 key=value key2="value with spaces"} at the end
      const attrRegex = /\s*\{([^}]+)\}\s*$/;
      const match = text.match(attrRegex);

      if (!match) return;

      const attrString = match[1];
      const hProperties: NonNullable<HeadingData["hProperties"]> = {};
      const classes: string[] = [];
      const attrOrder: string[] = [];

      // Parse the attribute string
      // Matches: #id, .class, key=value, key="value with spaces"
      // Key can contain colons (epub:type), hyphens (data-attr), or __COLON__ placeholder
      const tokenRegex =
        /#([\w-]+)|\.([^\s.#=]+)|([\w:-]+(?:__COLON__[\w-]+)*)=("[^"]*"|'[^']*'|[^\s]+)/g;
      let tokenMatch;

      while ((tokenMatch = tokenRegex.exec(attrString)) !== null) {
        if (tokenMatch[1]) {
          // #id
          hProperties.id = tokenMatch[1];
          attrOrder.push('id');
        } else if (tokenMatch[2]) {
          // .class
          classes.push(tokenMatch[2]);
          attrOrder.push(`class:${tokenMatch[2]}`);
        } else if (tokenMatch[3] && tokenMatch[4]) {
          // key=value - unescape __COLON__ placeholder back to ':'
          const key = tokenMatch[3].replace(/__COLON__/g, ':');
          let value = tokenMatch[4];
          // Remove quotes if present
          if (
            (value.startsWith('"') && value.endsWith('"')) ||
            (value.startsWith("'") && value.endsWith("'"))
          ) {
            value = value.slice(1, -1);
          }
          hProperties[key] = value;
          attrOrder.push(`key:${key}`);
        }
      }

      if (classes.length > 0) {
        hProperties.className = classes;
      }

      // Store original attribute order for round-trip fidelity
      if (attrOrder.length > 0) {
        hProperties._attrOrder = attrOrder;
      }

      // Only modify if we found attributes (excluding _attrOrder from count)
      const realKeys = Object.keys(hProperties).filter(k => k !== '_attrOrder');
      if (realKeys.length > 0) {
        // Remove the attribute string from the text
        (lastChild as Text).value = text.replace(attrRegex, "");

        // If the text is now empty, remove the text node
        if ((lastChild as Text).value === "") {
          node.children.pop();
        }

        // Store attributes in node.data.hProperties
        node.data = node.data || {};
        (node.data as HeadingData).hProperties = hProperties;
      }
    });
  };
};

export const remarkHeadingAttrsPlugin = $remark(
  "remarkHeadingAttrs",
  () => remarkHeadingAttrs,
);

withMeta(remarkHeadingAttrsPlugin.plugin, {
  displayName: "Remark<remarkHeadingAttrs>",
  group: "Remark",
});

withMeta(remarkHeadingAttrsPlugin.options, {
  displayName: "RemarkConfig<remarkHeadingAttrs>",
  group: "Remark",
});
